import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { LockUnlockService } from './lock-unlock.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { SubmissionService } from '../../submission-data/submission.service';

@Component({
    selector: 'gtw-efile-lock-unlock',
    templateUrl: './lock-unlock.component.html',
    styleUrls: ['./lock-unlock.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LockUnlockComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private subs = new Subscription();
  private isInitialized = false;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  is_locked: number = 0;
  saving: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private lockUnlockService: LockUnlockService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private submissionService: SubmissionService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = shell.userSettingsData;
    this.SERVER_CONFIG = shell.serverConfigData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    // Same row resolution as add-edit / efile-ctrlfn-dispatcher: prefer
    // SubmissionService.activeSubmission, else first selected grid row (not raw array).
    const rawRowPayload = rowPayloadFromActiveScreen(activeScreen);
    const gridRow = firstGridRowObjectFromPayload(rawRowPayload) || {};
    const activeSubmission: any = this.submissionService?.activeSubmission;
    if (activeSubmission?.SUBMISSION_ID != null && String(activeSubmission.SUBMISSION_ID).trim() !== '') {
      this.rowData = activeSubmission;
    } else {
      this.rowData = gridRow;
    }
    this.filterParams = normalizeFilterParams(activeScreen);
    // Keep UI aligned with the live submission row whenever context refreshes
    // (tab switch, setTabActive) — same source as legacy `selectedSubmission`.
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.syncIsLockedFromRowData();
    }
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    this.syncIsLockedFromRowData();
    this.cdr.detectChanges();
  }

  /**
   * Legacy `LockUnlockController` read `EfileFactory.selectedSubmission.IS_LOCKED`
   * (live object). We resolve `rowData` from `SubmissionService.activeSubmission`
   * in `applyWorkspaceContext`, but the API may send `IS_LOCKED` / `is_locked` as
   * number, string, or boolean — normalize like add-edit.
   */
  private syncIsLockedFromRowData(): void {
    this.is_locked = this.readIsLockedFromRow(this.rowData);
  }

  /** Returns 1 if locked, 0 if not (matches legacy truthy lock flag). */
  private readIsLockedFromRow(row: any): number {
    if (!row || typeof row !== 'object') {
      return 0;
    }
    const raw = row.IS_LOCKED ?? row.is_locked;
    if (raw === true || raw === 'true' || raw === 'Y' || raw === 'y') {
      return 1;
    }
    if (raw === false || raw === 'false' || raw === 'N' || raw === 'n' || raw === '' || raw == null) {
      return 0;
    }
    const n = Number(raw);
    if (!Number.isNaN(n)) {
      return n !== 0 ? 1 : 0;
    }
    return 0;
  }

  /**
   * Legacy `EfileElementsController.js` L3773–3774: after a successful save,
   * `EfileFactory.selectedSubmission.IS_LOCKED = vm.is_locked` so the rest of
   * the shell (sidebar conditions, add-edit banner) sees the new value. Mirror
   * that by patching the active submission and the matching tab row.
   */
  private persistIsLockedOnSubmissionObjects(locked: number): void {
    const sid = this.resolveSubmissionId();
    if (sid === '' || sid == null) {
      return;
    }
    const sidStr = String(sid);
    const sidNum = Number(sid);
    const idMatches = (id: any): boolean => {
      if (id === undefined || id === null) {
        return false;
      }
      return String(id) === sidStr || (Number.isFinite(sidNum) && Number(id) === sidNum);
    };
    const patch = (sub: any) => {
      if (!sub) {
        return;
      }
      const id = sub.SUBMISSION_ID ?? sub.submission_id;
      if (!idMatches(id)) {
        return;
      }
      sub.IS_LOCKED = locked;
      sub.is_locked = locked;
    };
    patch(this.submissionService.activeSubmission as any);
    for (const t of this.submissionService.submissionTabs || []) {
      patch(t as any);
    }
  }

  /**
   * Same fallback order as `EfileCtrlfnDispatcherService` (live filters →
   * globalParams → active submission) plus row keys used in add-edit
   * (`SUBMISSION_ID` / `submission_id`).
   */
  private resolveSubmissionId(): string | number {
    const rd = this.rowData || {};
    const fp = this.filterParams || {};
    const gs = this.globalService || {};
    const candidates = [
      rd.SUBMISSION_ID,
      rd.submission_id,
      fp.submission_id,
      fp.SUBMISSION_ID,
      gs.submission_id,
      gs.SUBMISSION_ID,
      this.submissionService?.activeSubmission?.SUBMISSION_ID,
    ];
    for (const c of candidates) {
      if (c == null) {
        continue;
      }
      const s = String(c).trim();
      if (s !== '' && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined') {
        return c as string | number;
      }
    }
    return '';
  }

  lockUnlock(): void {
    if (this.saving) {
      return;
    }

    this.saving = true;
    this.cdr.detectChanges();

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs.api;
    const gp = this.gtwGlobal?.global?.globalParams || {};
    const scenario =
      this.rowData.SCENARIO ??
      this.filterParams?.scenario ??
      this.filterParams?.SCENARIO ??
      gp.scenario ??
      '74';
    const taxYear =
      this.rowData.TAX_YEAR ??
      this.filterParams?.tax_year ??
      gp.tax_year;
    const jcdRaw =
      this.rowData.JCD_KEY ??
      this.filterParams?.jcd_key ??
      this.filterParams?.JCD_KEY ??
      gp.jcd_key ??
      250;
    const jcdNum = Number(jcdRaw);
    const jcdKey = !Number.isNaN(jcdNum) && jcdNum > 0 ? jcdNum : 250;

    // kzksix workspace contract (application/x-www-form-urlencoded)
    const params: any = {
      submission_id: this.resolveSubmissionId(),
      lock_unlock: this.is_locked ? 0 : 1,
      tax_year: taxYear,
      scenario,
      jcd_key: jcdKey,
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      issue_id: gp.issue_id ?? this.filterParams?.issue_id ?? '',
      issue_short_desc: gp.issue_short_desc ?? this.filterParams?.issue_short_desc ?? '',
      checkLockedbj: JSON.stringify([{ scenario_key: String(scenario) }]),
      scenarioCode:
        this.filterParams?.scenarioCode ??
        this.filterParams?.scenario_code ??
        gp.scenarioCode ??
        'RAF',
      is_issue_key_enabled:
        this.filterParams?.is_issue_key_enabled ??
        gp.is_issue_key_enabled ??
        'N',
    };

    this.lockUnlockService.lockUnlock(baseUrl, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        const callSuccess = response?.callSuccess ?? response?.data?.callSuccess;
        if (callSuccess === '0') {
          this.gtwSnackbarService.openSnackBar('Something went wrong while saving the data!', 'gtw-snackbar--red');
        } else {
          this.is_locked = this.is_locked ? 0 : 1;
          this.persistIsLockedOnSubmissionObjects(this.is_locked);
          const msg = this.is_locked ? 'Locked successfully!' : 'Unlocked successfully!';
          this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.saving = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.gtwSnackbarService.openSnackBar('Something went wrong while saving the data!', 'gtw-snackbar--red');
        this.saving = false;
        this.cdr.detectChanges();
      }
    );
  }
}
